import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { INotification, IReply } from '../../../../../models/template/notifications-model.interface';

@Component({
  selector: 'app-review-comment-card-v2',
  imports: [CommonModule, FormsModule],
  templateUrl: './review-comment-card-v2.component.html',
  styleUrl: './review-comment-card-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewCommentCardV2Component {
  @Input({ required: true }) comment!: INotification;
  @Input() number: number | null = null;
  @Input() currentUsername: string | null = null;
  @Input() active = false;

  @Output() toggleDone = new EventEmitter<void>();
  @Output() saveEdit = new EventEmitter<string>();
  @Output() deleteComment = new EventEmitter<void>();
  @Output() annotate = new EventEmitter<void>();
  @Output() showAnnotation = new EventEmitter<void>();
  @Output() addReply = new EventEmitter<string>();
  @Output() saveReply = new EventEmitter<{ replyId: string; text: string }>();

  menuOpen = false;
  editing = false;
  editText = '';
  replyOpen = false;
  replyDraft = '';

  constructor(private elRef: ElementRef) {}

  get isAuthor(): boolean {
    const commentUser = this.comment.userInfo?.username;
    const currentUser = this.currentUsername;
    return !!(commentUser && currentUser && commentUser.toLowerCase() === currentUser.toLowerCase());
  }

  get isPinned(): boolean {
    return this.comment.xPercent != null && this.comment.yPercent != null;
  }

  get isCompleted(): boolean {
    return !!this.comment.completed;
  }

  get canAnnotate(): boolean {
    return this.comment.version != null;
  }

  get replies(): IReply[] {
    return this.comment.replys ?? [];
  }

  get replyMeta(): string {
    const count = this.replies.length;
    if (count === 0) return 'No replies';
    if (count === 1) return '1 reply';
    return `${count} replies`;
  }

  canEditReply(reply: IReply): boolean {
    const replyUser = reply.userInfo?.username;
    const currentUser = this.currentUsername;
    return !!(replyUser && currentUser && replyUser.toLowerCase() === currentUser.toLowerCase());
  }

  toggleMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen && !this.elRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
  }

  startEdit(): void {
    this.menuOpen = false;
    this.editing = true;
    this.editText = this.comment.notificationText;
  }

  submitEdit(): void {
    const text = this.editText.trim();
    if (!text) return;
    this.saveEdit.emit(text);
    this.editing = false;
  }

  cancelEdit(): void {
    this.editing = false;
  }

  submitReply(): void {
    const text = this.replyDraft.trim();
    if (!text) return;
    this.addReply.emit(text);
    this.replyDraft = '';
    this.replyOpen = false;
  }

  onReplyKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submitReply();
    }
  }

  commitReplyEdit(reply: IReply, value: string): void {
    if (!this.canEditReply(reply) || !reply.replyId) return;
    const text = value.trim();
    if (!text || text === reply.replyText) return;
    this.saveReply.emit({ replyId: reply.replyId, text });
  }

  onToggleDone(): void {
    this.menuOpen = false;
    this.toggleDone.emit();
  }

  onAnnotate(): void {
    this.menuOpen = false;
    this.annotate.emit();
  }

  onDelete(): void {
    this.menuOpen = false;
    this.deleteComment.emit();
  }
}
